import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  BellContained,
  BellOutlined,
  IosArrowHorizontalScroller,
  Menu,
  MenuItem,
  Share,
  useDocumentClick,
  useDocumentEvent,
  useLatest,
} from '@sgcmpteltd/uikit-react';
import {
  Result,
  RESULT_VARIANT_ERROR,
  RESULT_VARIANT_SUCCESS,
} from '@sgcmpteltd/uikit-react/components/result';
import {
  DOCUMENT_EVENT_NAME_MOUSEDOWN,
} from '@sgcmpteltd/uikit-react/hooks/use-document-event';
import cx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ACTION_STATUS_ERROR, ACTION_STATUS_SUCCESS } from '@/actions';
import {
  reportError,
} from '@/actions/misc/home/report-error/new-car-promotion/post';
import {
  ListTypeLabel,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/listing-type-label'; // @suppress longLineCheck
import { ContactItem } from '@/components/contact-item';
import { ImageWithFallback } from '@/components/image-with-fallback';
import { TextLink } from '@/components/text-link';
import { Tooltip } from '@/components/tooltip';
import { SITE_BASE_PATH } from '@/configs/site';
import { UserContext } from '@/context/user';
import {
  getDesktopContactNumbers,
  getMobileContactNumber,
} from '@/utils/dealerContacts/util';
import { getFormattedDateString } from '@/utils/string/formattedDate';
import {
  joinSgcmMedia,
  joinSgcmMobileSite,
  joinSgcmMsSite,
  joinSgcmSite,
} from '@/utils/url/join/public';
import { createShareLinkUrl } from '@/utils/url/social-media/whats-app';

import { Step as ReportErrorContent } from '../report-error';
import {
  AD_FEATURE_TABS_SCROLL_END_TOLERANCE,
  ICON_SIZE_IN_PIXEL,
  LIST_TYPE_LABEL_SIZE,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
  PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
  RECOMMENDED_PRICE_DESC_1,
  RECOMMENDED_PRICE_DESC_2,
} from './constants';
import styles from './styles.module.scss';
import type { Component, ReportErrorResult } from './types';
import {
  concatenateValidFeatures,
  filterDealersBySubModelId,
  parseHtmlStringIntoArray,
} from './utils';

const isBrowser = () => typeof window !== 'undefined';

const convertPriceToText = (priceInt: number | null, unit?: string) => {
  if (priceInt === null) {
    return '-';
  }

  if (priceInt === 0) {
    return 'POA';
  }

  if (unit) {
    return `$${priceInt.toLocaleString()}${unit}`;
  }

  return `$${priceInt.toLocaleString()}`;
};

export const PiPriceListContent: Component = ({
  carId,
  slug,
  modalStep,
  piPriceListData,
  submodelId,
  modelIsShortlisted,
  onOpenAlertModal,
  onOpenReportErrorModal,
  onOpenRecommendedPirceModal,
  onOpenAdditionalInfoModal,
  onAddAdditionalInfo,
  onHandleSetStep,
}) => {
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const currentUrl = window.location.href;
  const router = useRouter();
  const userContextData = useContext(UserContext);
  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');
  const [menuOpen, setIsMenuOpen] = useState(false);
  const [
    reportErrorResultData, setReportErrorResultData,
  ] = useState<ReportErrorResult | null>(null);

  const shareUrlButtonElRef = useRef<HTMLButtonElement | null>(null);
  const shareMenuRef = useRef<HTMLMenuElement | null>(null);
  const scrollerContentContainerElRef = useRef<HTMLDivElement | null>(null);
  const [isReportErrorModalOpen, setIsReportErrorModalOpen] = useState(false);

  useEffect(() => {
    if (scrollerContentContainerElRef.current) {
      scrollerContentContainerElRef.current.onscroll = () => {
        setIsMenuOpen(false);
      };
    }
  }, []);

  const {
    ref: onDocumentClickRef,
  } = useLatest<Document['onclick']>({
    value: ({ target }) => {
      const { current: shareUrlButtonEl } = shareUrlButtonElRef;

      if (
        !shareUrlButtonEl
        || !(target instanceof HTMLElement)
        || !document.documentElement.contains(target)
        || shareUrlButtonEl.contains(target)
      ) {
        return;
      }

      setIsMenuOpen(false);
    },
  });

  const {
    ref: onDocMouseDownRef,
  } = useLatest<Document['onmousedown']>({

    value: ({ target }) => {
      const { current: shareUrlMenuEl } = shareMenuRef;

      if (
        isDesktop()
      ) {
        return;
      }

      if (
        isDesktop()
        || !shareUrlMenuEl
        || !(target instanceof HTMLElement)
        || !document.documentElement.contains(target)
        || shareUrlMenuEl.contains(target)
      ) {
        return;
      }

      setIsMenuOpen(false);
    },
  });

  const {
    ref: onDocScrollRef,
  } = useLatest<Document['onscroll']>({
    value: () => {
      if (
        isDesktop()
      ) {
        return;
      }
      setIsMenuOpen(false);
    },
  });

  useDocumentClick({
    onClickRef: onDocumentClickRef,
  });

  useDocumentEvent({
    eventName: DOCUMENT_EVENT_NAME_MOUSEDOWN,
    eventHandlerRef: onDocMouseDownRef,
  });

  useDocumentEvent({
    eventName: 'scroll',
    eventHandlerRef: onDocScrollRef,
  });

  const {
    subModels, parallelDealers,
  } = piPriceListData;

  const targetSubmodel = subModels.find(
    submodel => submodel.id === submodelId,
  );

  const featuresInString = targetSubmodel
    ? concatenateValidFeatures(targetSubmodel.features, targetSubmodel.specifications) // @suppress longLineCheck
    : '';
  const additionFeaturesArray = targetSubmodel?.features.additionalFeatures
    ? parseHtmlStringIntoArray(targetSubmodel.features.additionalFeatures)
    : [];

  const filteredDealers = parallelDealers && filterDealersBySubModelId(parallelDealers, submodelId); // @suppress longLineCheck
  const sortedDealers = filteredDealers
    ? filteredDealers.sort((a, b) => {
      const aAdvert = a.adverts.find(
        advert => advert.subModelId === submodelId,
      );
      const bAdvert = b.adverts.find(
        advert => advert.subModelId === submodelId,
      );

      if (!aAdvert?.price) {
        return 1;
      }

      if (!bAdvert?.price) {
        return -1;
      }

      return aAdvert.price - bAdvert.price;
    })
    : [];

  const handleAlertClick = () => {
    onOpenAlertModal();
  };

  const handleSubmitReviewButtonClick = () => {
    if (userContextData?.userId) {
      router.push(`${joinSgcmMsSite(SITE_BASE_PATH ?? '')}/info/${carId}/${slug}/submit-review`);
    } else {
      window.open(
        joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
        '_self',
      );
    }
  };

  const handleReportErrorClick = () => {
    onOpenReportErrorModal();
    setIsReportErrorModalOpen(true);
  };

  const handlePriceListClick = () => {
    window.open(joinSgcmSite(`/new_cars/newcars_PI_carpricelistprint.php?CarCode=${piPriceListData.id}&Subcode=${submodelId}&Subsubcode=all`), '_blank');
  };

  const handleRecommendedPriceModalClick = () => {
    if (isDesktop()) {
      return;
    }

    onOpenRecommendedPirceModal?.();
  };

  const handleAdditionalInfoModalClick = (remarkContent: string | null) => {
    if (isDesktop()) {
      return;
    }

    onAddAdditionalInfo?.(remarkContent);
    onOpenAdditionalInfoModal?.();
  };

  const handleShareClick = () => {
    setIsMenuOpen(prev => !prev);
  };

  const handleCopyLinkClick = () => {
    const link = joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${piPriceListData.id}&Subcode=${submodelId}`).toString();

    navigator.clipboard.writeText(link);
    // eslint-disable-next-line no-alert
    window.alert('Link copied to clipboard');
  };

  useEffect(() => {
    if (menuOpen && shareMenuRef.current && shareUrlButtonElRef.current) {
      const rect = shareUrlButtonElRef.current.getBoundingClientRect();

      shareMenuRef.current.style.top = `${rect.top + rect.height}px`;
      shareMenuRef.current.style.left = `${rect.left - (isDesktop() ? 0 : (shareMenuRef.current.getBoundingClientRect().width - rect.width))}px`;
    }
  }, [isDesktop, menuOpen]);

  return (
    <>
      <div className={cx(
        styles.containerModalContent,
        styles.containerPiPricelistModal,
      )}>
        <Menu
          ref={shareMenuRef}
          className={cx(
            menuOpen ? styles.isShow : styles.isHidden,
            styles.containerMenu,
            styles.isFit,
          )}>
          <MenuItem
            onClick={handleCopyLinkClick}>
            Copy Link
          </MenuItem>
          <MenuItem>
            <a
              className={styles.containerShareLink}
              href={`${createShareLinkUrl({
                text: currentUrl,
              })}`}
              rel="noreferrer noopener"
              target="_blank">
              WhatsApp Share
            </a>
          </MenuItem>
        </Menu>
        <IosArrowHorizontalScroller
          arrowLeftContainerClassName={cx(
            styles.containerNavBarArrow,
            styles.isLeft,
          )}
          arrowRightContainerClassName={cx(
            styles.containerNavBarArrow,
            styles.isRight,
          )}
          contentContainerElRef={scrollerContentContainerElRef}
          scrollDistance={({ clientWidth }) => clientWidth}
          scrollEndTolerance={AD_FEATURE_TABS_SCROLL_END_TOLERANCE}>
          <ul className={styles.containerFeatureRow}>
            <button
              className={styles.buttonPiFeature}
              onClick={handleAlertClick}>
              {modelIsShortlisted
                ? (
                  <BellContained
                    className={styles.buttonAdFeature}
                    size={ICON_SIZE_IN_PIXEL} />
                )
                : (
                  <BellOutlined
                    className={styles.buttonAdFeature}
                    size={ICON_SIZE_IN_PIXEL} />
                )}
              <span className={styles.textModalFeature}>
                Alert me
              </span>
            </button>
            <button
              className={styles.buttonPiFeature}
              onClick={handleSubmitReviewButtonClick}>
              <Image
                alt="report error icon image"
                className={styles.imageThumbnail}
                height={ICON_SIZE_IN_PIXEL}
                src={`${joinSgcmMedia('/public/new-cars/icon/iconSubmitReview.svg')}`}
                width={ICON_SIZE_IN_PIXEL} />
              <span className={styles.textModalFeature}>
                Submit Review
              </span>
            </button>
            <button
              className={styles.buttonPiFeature}
              onClick={handleReportErrorClick}>
              <Image
                alt="icon image"
                className={styles.imageThumbnail}
                height={ICON_SIZE_IN_PIXEL}
                src={`${joinSgcmMedia('/public/new-cars/icon/iconExclamationMark.svg')}`}
                width={ICON_SIZE_IN_PIXEL} />
              <span className={styles.textModalFeature}>
                Report Error
              </span>
            </button>
            <button
              className={styles.buttonPiFeature}
              onClick={handlePriceListClick}>
              <Image
                alt="pricelist icon"
                height={ICON_SIZE_IN_PIXEL}
                src={`${joinSgcmMedia('/public/new-cars/icon/pricelist.svg')}`}
                title="pricelist"
                width={ICON_SIZE_IN_PIXEL} />
              <span className={styles.textModalFeature}>
                Printable Pricelist
              </span>
            </button>
            <button
              ref={shareUrlButtonElRef}
              className={styles.buttonPiFeature}
              onClick={handleShareClick}>
              <div
                className={styles.containerShareLinkIcon}>
                <Share
                  className={styles.buttonAdFeature}
                  size={ICON_SIZE_IN_PIXEL} />
              </div>
              <span className={styles.textModalFeature}>
                Share
              </span>
            </button>
          </ul>
        </IosArrowHorizontalScroller>
        <div className={styles.containerSubmodelDetail}>
          <div className={styles.textSubmodelTitle}>Submodel</div>
          <div className={styles.textSubmodelFullName}>
            {targetSubmodel?.name}
          </div>
          <div className={styles.containerAveragePrice}>
            $
            {targetSubmodel?.price !== null
            && targetSubmodel?.price !== undefined
              ? (Math.ceil(targetSubmodel.price / 100) * 100)
                .toLocaleString()
              : ''}
            <div className={styles.containerDesc}>
              This is the recommended retail price provided by Sgcarmart
              {isDesktopMode && (
                <Tooltip
                  className={styles.containerTooltip}
                  content={(
                    <div className={cx(
                      styles.containerTooltipContent,
                      styles.containerRecommendedPriceTooltip,
                    )}>
                      <div className={
                        styles.containerRecommendedPriceContent
                      }>
                        <div className={styles.textTooltipTitle}>
                          Recommended Retail Price
                        </div>
                        <div>
                          <div>
                            {RECOMMENDED_PRICE_DESC_1}
                          </div>
                          <br />
                          <div>
                            {RECOMMENDED_PRICE_DESC_2}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}>
                  <button
                    className={styles.buttonRecommendedPriceDesc}
                    onClick={handleRecommendedPriceModalClick}>
                    <ImageWithFallback
                      alt="info icon image"
                      className={cx(
                        styles.imageHoverIcon,
                        styles.imageInfoIcon,
                      )}
                      height={14}
                      src={`${joinSgcmMedia('/public/new-cars/icon/iconOutlineInfo.svg')}`}
                      width={14}
                      priority />
                  </button>
                </Tooltip>
              )}
              {!isDesktopMode && (
                <button
                  className={styles.buttonRecommendedPriceDesc}
                  onClick={handleRecommendedPriceModalClick}>
                  <ImageWithFallback
                    alt="info icon image"
                    className={cx(
                      styles.imageHoverIcon,
                      styles.imageInfoIcon,
                    )}
                    height={14}
                    src={`${joinSgcmMedia('/public/new-cars/icon/iconOutlineInfo.svg')}`}
                    width={14}
                    priority />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.containerModelFeatures}>
          <div className={styles.textSubmodelTitle}>Features</div>
          <div className={styles.textFeatures}>
            {featuresInString}
            <ul className={styles.containerAdditionFeatures}>
              {additionFeaturesArray.map(item => (
                <li key={`feature-${item}`}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className={styles.containerAvaliableDealerList}>
          <div className={styles.containerHeaderRow}>
            <div>Dealer</div>
            <div>Price</div>
          </div>
          <div className={styles.containerAdverts}>
            {sortedDealers?.map((dealer) => {
              const selectedAdvert = dealer.adverts.find(
                advert => advert.subModelId === submodelId,
              );
              const {
                hotlines: desktopHotlines,
              } = getDesktopContactNumbers(dealer.hotlines, dealer.whatsAppNumber); // @suppress longLineCheck
              const { contactNumber } = getMobileContactNumber(dealer.hotlines, dealer.whatsAppNumber); // @suppress longLineCheck
              const lastUpdatedDateString = getFormattedDateString(dealer.updatedAt, 'shorten'); // @suppress longLineCheck
              const hasStock = selectedAdvert?.stocks
                && selectedAdvert.stocks.length > 0;
              const hasRemark = selectedAdvert?.remark
                && selectedAdvert.remark.length > 0;
              const remarkContent = selectedAdvert?.remark || null;

              return (
                <div
                  key={`dealer-${dealer.id}`}
                  className={styles.containerAdvert}>
                  <div className={styles.containerLeftColumn}>
                    <div className={styles.containerAdvertTitle}>
                      <TextLink
                        className={styles.textPiDealerLink}
                        href={isDesktop()
                          ? `${joinSgcmSite(`/new_cars/newcars_PIdealer.php?dcode=${dealer.id}`)}`
                          : `${joinSgcmMobileSite(`/new_cars/PIdealer.php?dcode=${dealer.id}`)}`}
                        target="_self"
                        text={dealer.name} />
                      {hasStock
                      && (
                        <ListTypeLabel
                          className={styles.containerHasStockLabel}
                          labelSize={LIST_TYPE_LABEL_SIZE}
                          type="readyStocks" />
                      )}
                    </div>
                    <div className={styles.containerHotlines}>
                      <ContactItem
                        contactType="hotline"
                        displayMode="textOnly"
                        fontSize="small"
                        list={isDesktopMode
                          ? desktopHotlines.slice().reverse()
                          : [contactNumber]}
                        separator="/" />
                    </div>
                    <div className={styles.textLastUpdated}>
                      Updated:
                      {' '}
                      {lastUpdatedDateString}
                    </div>
                    <div />
                  </div>
                  <div className={styles.containerLeftColumn}>
                    <div className={styles.containerAdvertPrice}>
                      {selectedAdvert
                        ? (
                          <div>
                            {convertPriceToText(selectedAdvert.price)}
                          </div>
                        )
                        : <div>POA</div>}
                      {hasRemark && isDesktopMode
                      && (
                        <Tooltip
                          className={cx(
                            styles.containerTooltip,
                            styles.containerRemarkTooltip,
                          )}
                          content={(
                            <div className={cx(
                              styles.containerTooltipContent,
                              styles.containerRemarkTooltipContent,
                            )}>
                              <div className={styles.containerRemarkContent}>
                                {remarkContent}
                              </div>
                            </div>
                          )}>
                          <ImageWithFallback
                            alt="icon image"
                            className={styles.imageHoverIcon}
                            height={14}
                            src={`${joinSgcmMedia('/public/new-cars/icon/iconRedAdd.svg')}`}
                            width={14}
                            priority />
                        </Tooltip>
                      )}
                      {hasRemark && !isDesktopMode
                      && (
                        <button
                          className={styles.buttonAdditionalInfo}
                          onClick={
                            () => handleAdditionalInfoModalClick(remarkContent) // @suppress longLineCheck
                          }>
                          <ImageWithFallback
                            alt="icon image"
                            className={styles.imageHoverIcon}
                            height={14}
                            src={`${joinSgcmMedia('/public/new-cars/icon/iconRedAdd.svg')}`}
                            width={14}
                            priority />
                        </button>
                      )}
                    </div>
                    <div className={styles.textPricingSchema}>
                      {dealer.pricingSchema}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Modal
        className={cx(
          styles.containerRoot,
          styles.isNormal,
          modalStep === 'errorSubmitResult' && styles.isSubmitted,
        )}
        contentClassName={styles.containerModal}
        headerClassName={cx(
          styles.containerModalHeader,
          styles.isCustom,
        )}
        headerControlClassName={styles.headerControlClassName}
        isOpen={isReportErrorModalOpen}
        title=""
        onClose={() => setIsReportErrorModalOpen(false)}>
        <div
          className={styles.textStepTitle}>
          Report Error
        </div>
        {modalStep === 'reportError'
        && (
          <ReportErrorContent
            onSubmit={async ({
              email: errorReportEmail,
              description: errorReportDescription,
              name: errorReportSenderName,
            }) => {
              const formData = new FormData();

              formData.set('email', errorReportEmail);
              formData.set('description', errorReportDescription);
              formData.set('link', currentUrl.toString());
              formData.set('name', errorReportSenderName);

              const actionResult = await reportError(formData);

              switch (actionResult.status) {
                case ACTION_STATUS_SUCCESS:
                  setReportErrorResultData({
                    isSuccess: true,
                    title: PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
                    message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
                  });

                  break;
                case ACTION_STATUS_ERROR:
                default:
                  setReportErrorResultData({
                    isSuccess: false,
                    title: PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                    message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                  });
                  break;
              }

              onHandleSetStep('errorSubmitResult');
            }} />
        )}
        {modalStep === 'errorSubmitResult' && reportErrorResultData
        && (
          <Result
            message={reportErrorResultData.message}
            title={reportErrorResultData.title}
            variant={
              reportErrorResultData.isSuccess
                ? RESULT_VARIANT_SUCCESS
                : RESULT_VARIANT_ERROR
            } />
        )}
      </Modal>
    </>
  );
};
